import { CartLinesUpdateEvent } from '@shopify/events';

/** the cart's failed-add note keeper (see showError) @type {MutationObserver | undefined} */
let noteObserver;

/**
 * Wings: "Pairs with" add buttons in the cart drawer and on the cart page. Adds one unit the same
 * way Atelier's product form does (cart add with section HTML, then a standard
 * CartLinesUpdateEvent), so the cart, the header count and the free-shipping line all refresh
 * exactly as after any other add.
 */
document.addEventListener('click', async (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-wings-pair-add]') : null;
  if (!(button instanceof HTMLElement) || button.getAttribute('aria-busy') === 'true') return;
  event.preventDefault();
  // a new try: an earlier failure's note stops coming back once the cart refreshes
  noteObserver?.disconnect();

  // pieces with options: the chosen variant comes from the card's picker
  const select = button.dataset.wingsPairSelect ? document.getElementById(button.dataset.wingsPairSelect) : null;
  const id = select instanceof HTMLSelectElement ? select.value : button.dataset.wingsPairAdd;
  if (!id) return;
  button.setAttribute('aria-busy', 'true');
  // a new try clears the last failure's note (outside the cart it sits in the card, and the
  // Shop the look panel stays open while one is showing)
  if (!cartOf(button)) button.closest('.wings-pairs__card')?.querySelector('.wings-pairs__error')?.remove();

  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
    .filter(Boolean);

  const deferred = CartLinesUpdateEvent.createPromise();
  button.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: id, quantity: 1 }],
      promise: deferred.promise,
    })
  );

  try {
    const body = new FormData();
    body.append('id', id);
    body.append('quantity', '1');
    // a piece on sale carries the final-sale note to checkout (snippets/wings-final-sale-property.liquid)
    const saleSource = select instanceof HTMLSelectElement ? select.selectedOptions[0] : button;
    if (saleSource instanceof HTMLElement && saleSource.dataset.finalSaleName) {
      body.append(`properties[${saleSource.dataset.finalSaleName}]`, saleSource.dataset.finalSaleValue || '');
    }
    if (sectionIds.length) body.append('sections', sectionIds.join(','));

    const addUrl = Theme.routes.cart_add_url.endsWith('.js') ? Theme.routes.cart_add_url : `${Theme.routes.cart_add_url}.js`;
    const added = await fetch(addUrl, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body,
    });
    const response = await added.json().catch(() => ({ status: added.status }));

    const ajaxCart = await fetch(`${Theme.routes.cart_url}.js`).then((r) => r.json());
    const failed = Boolean(response.status);
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
      detail: {
        items: ajaxCart.items,
        source: 'wings-cart-pairs',
        sections: failed ? undefined : response.sections,
        didError: failed,
      },
    });
    // Shopify's stock messages read well ("You can't add more…"); anything technical gets our line
    if (failed) showError(button, response.status === 422 ? response.description || response.message : undefined);
    // in the cart the drawer redraws the cards; elsewhere (Shop the look) the button frees up here,
    // which is also the panel's cue to close so the cart is in front
    else if (!cartOf(button)) button.removeAttribute('aria-busy');
  } catch (error) {
    deferred.reject(error);
    showError(button);
  }
});

// the picker's visible face shows the chosen words (the native select over it is invisible)
document.addEventListener('change', (event) => {
  const select = event.target;
  if (!(select instanceof HTMLSelectElement) || !select.classList.contains('wings-pairs__select')) return;
  const face = select.parentElement?.querySelector('.wings-pairs__select-face');
  if (face) face.textContent = select.selectedOptions[0]?.textContent ?? '';
});

/**
 * A failed add (sold out a moment ago, quantity limit, network) never leaves the card stuck:
 * the button comes back and a short line says why, until the shopper tries again.
 * @param {HTMLElement} button
 * @param {string} [message]
 */
function showError(button, message) {
  // the same cards outside the cart (Shop the look panel): the note goes straight into the card,
  // before the button frees up, so the panel can tell the add failed and stay open
  const ownCard = button.closest('.wings-pairs__card');
  if (ownCard && !cartOf(ownCard)) {
    writeNote(ownCard, message);
    return;
  }
  button.removeAttribute('aria-busy');
  // in the cart the note runs under the whole row, so no card's Add moves, and names the piece
  const name = ownCard?.querySelector('.wings-pairs__name')?.textContent?.trim();
  const text =
    message ||
    (name ? `Couldn't add ${name} just now. Please try again.` : "Couldn't add this just now. Please try again.");
  const cart = cartOf(button);
  if (!cart) return;
  const place = () => {
    const block = cart.querySelector('.wings-pairs:not(.wings-pairs--empty)');
    if (!block) return;
    let note = block.querySelector(':scope > .wings-pairs__error');
    if (!note) {
      note = document.createElement('p');
      note.className = 'wings-pairs__error';
      note.setAttribute('role', 'status');
      block.append(note);
    }
    if (note.textContent !== text) note.textContent = text;
  };
  place();
  // Atelier refreshes the cart after a failed add to resync it, whenever its request returns;
  // the note is put back if that refresh replaces it (a fixed wait lost it on slow replies)
  noteObserver?.disconnect();
  const observer = new MutationObserver(place);
  observer.observe(cart, { childList: true, subtree: true });
  noteObserver = observer;
  setTimeout(() => observer.disconnect(), 4000);
}

/**
 * The cart a button sits in: the drawer, or the cart page's section (which a refresh keeps).
 * Outside a cart (Shop the look) there is none.
 * @param {Element} el
 * @returns {Element | null}
 */
function cartOf(el) {
  return el.closest('#cart-drawer') || el.closest('.cart-page')?.closest('.shopify-section') || null;
}

/**
 * @param {Element} card
 * @param {string} [message]
 */
function writeNote(card, message) {
  card.querySelector('.wings-pairs__add')?.removeAttribute('aria-busy');
  let note = card.querySelector('.wings-pairs__error');
  if (!note) {
    note = document.createElement('p');
    note.className = 'wings-pairs__error';
    note.setAttribute('role', 'status');
    card.querySelector('.wings-pairs__actions')?.append(note);
  }
  note.textContent = message || "Couldn't add this just now. Please try again.";
}
